import { useMutation } from '@apollo/client';

import { useCompleteFragment } from '@eluve/apollo-client';
import {
  TenantUserRoleFragmentDoc,
  UpdateUserTenantRoleDocument,
} from '@eluve/client-gql-operations';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  useToast,
} from '@eluve/components';

type RoleSelect = {
  tenantId: string;
  userId: string;
};

const useTenantUserRole = (tenantId: string, userId: string) => {
  const data = useCompleteFragment({
    fragment: TenantUserRoleFragmentDoc,
    key: {
      tenantId,
      userId,
    },
    strict: false,
  });

  return data?.role ?? '';
};

export const RoleSelector: React.FC<RoleSelect> = ({ tenantId, userId }) => {
  const toast = useToast();
  const [updateUserTenantRole] = useMutation(UpdateUserTenantRoleDocument, {
    onError: () => toast.error('Error updating user role'),
    optimisticResponse: ({ tenantId, userId, role: newRole }) => {
      return {
        updateTenantUsersByPk: {
          __typename: 'TenantUsers' as const,
          tenantId,
          userId,
          role: newRole,
        },
      };
    },
  });

  const updateRole = async (
    tenantId: string,
    userId: string,
    newRole: string,
  ) => {
    await updateUserTenantRole({
      variables: {
        tenantId,
        userId,
        role: newRole,
      },
    });
  };

  const tenantRole = useTenantUserRole(tenantId, userId);

  return (
    <div className="flex justify-center">
      <Select
        value={tenantRole}
        onValueChange={(newRole: string) => {
          if (newRole === tenantRole) {
            return;
          }
          return updateRole(tenantId, userId, newRole);
        }}
      >
        <SelectTrigger className="text-text-primary">
          {tenantRole}
        </SelectTrigger>
        <SelectContent>
          <SelectItem
            className="focus:bg-bg-accent-quaternary"
            value="tenant-admin"
          >
            tenant-admin
          </SelectItem>
          <SelectItem
            className="focus:bg-bg-accent-quaternary"
            value="tenant-member"
          >
            tenant-member
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};
